import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { FaDownload, FaPrint } from "react-icons/fa";

// Метатеги
const MetaTags = () => (
  <>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="description" content="Контактная информация и карта ООО «Сонет-Дом». Как связаться с нами и где нас найти." />
    <meta name="keywords" content="Сонет-Дом, контакты, карта, связаться, адрес, телефон, электронная почта" />
  </>
);

// Глобальные стили для предотвращения горизонтального скроллинга
const GlobalStyles = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f4f4f4;
  color: #333;
  transition: background 0.5s ease;
  h1, p, button {
    z-index: 1;
    position: relative;
    text-align: center;
  }
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Заголовок
const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: #007aff;
  animation: fadeIn 1s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

// Контейнер для информации о контактах и карты
const InfoAndMapContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 1200px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    padding: 1.5rem;
  }
`;

// Контейнер для информации о контактах
const ContactContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  text-align: left; /* Выравнивание текста по левому краю */
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

// Подзаголовок
const Subtitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #333;
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }
`;

// Адрес
const Address = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
`;

// Контактная информация
const ContactInfo = styled.a`
  display: block; /* Каждый элемент занимает всю ширину */
  font-size: 1rem;
  color: #007aff;
  margin-bottom: 0.5rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

// Контейнер для времени работы
const WorkingHoursContainer = styled.div`
  margin-top: 1rem;
  @media (max-width: 768px) {
    margin-top: 0.75rem;
  }
`;

// Таблица времени работы
const WorkingHoursTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  @media (max-width: 768px) {
    margin-top: 0.75rem;
  }
`;

// Заголовок таблицы
const TableHeader = styled.th`
  background-color: #007aff;
  color: white;
  padding: 0.5rem;
  text-align: left;
  @media (max-width: 768px) {
    padding: 0.4rem;
  }
`;

// Ячейка таблицы
const TableCell = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #ddd;
  @media (max-width: 768px) {
    padding: 0.4rem;
  }
`;

// Контейнер для карты
const MapContainer = styled.div`
  width: 100%;
  max-width: 600px;
  iframe {
    width: 100%;
    height: 400px;
    border: none;
  }
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Contacts = () => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const infoAndMapVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  // Эффект для прокрутки страницы вверх при загрузке
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <MetaTags />
      <GlobalStyles>
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="#f4f4f4"
          textColor="#333"
          style={{ marginTop: '60px' }} // Добавляем отступ сверху
        >
          <Title>Контакты</Title>
          <InfoAndMapContainer
            variants={infoAndMapVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <ContactContainer>
              <Subtitle>Офис в г. Солнечногорск</Subtitle>
              <Address>
                141506 Московская область, г.Солнечногорск, ул.Красная, д.58, офис № 25, эт.7
              </Address>
              <ContactInfo href="tel:+74959941398">Тел: +7 (495) 994-1398</ContactInfo>
              <ContactInfo href="tel:+74962649045">Тел: +7 (4962) 64-90-45</ContactInfo>
              <ContactInfo href="mailto:mail@sonet-dom.ru">E-Mail: mail@sonet-dom.ru</ContactInfo>
              <WorkingHoursContainer>
                <Subtitle>Время работы:</Subtitle>
                <WorkingHoursTable>
                  <thead>
                    <tr>
                      <TableHeader>Дни недели</TableHeader>
                      <TableHeader>Время</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <TableCell>Понедельник - Четверг</TableCell>
                      <TableCell>09:00 - 18:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Пятница</TableCell>
                      <TableCell>09:00 - 17:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Обед</TableCell>
                      <TableCell>Без обеда</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Суббота (дежурный день)</TableCell>
                      <TableCell>10:00 - 14:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Воскресенье</TableCell>
                      <TableCell>Выходной</TableCell>
                    </tr>
                  </tbody>
                </WorkingHoursTable>
              </WorkingHoursContainer>
            </ContactContainer>
            <MapContainer>
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                <a href="https://yandex.ru/maps/org/sonet_dom/1027510868/?utm_medium=mapframe&utm_source=maps" style={{ color: '#eee', fontSize: '12px', position: 'absolute', top: '0px' }}>Сонет Дом</a>
                <a href="https://yandex.ru/maps/10755/solnechnogorsk/category/security_and_alarm_systems/184105350/?utm_medium=mapframe&utm_source=maps" style={{ color: '#eee', fontSize: '12px', position: 'absolute', top: '14px' }}>Системы безопасности и охраны в Солнечногорске</a>
                <a href="https://yandex.ru/maps/10755/solnechnogorsk/category/intercoms/184105336/?utm_medium=mapframe&utm_source=maps" style={{ color: '#eee', fontSize: '12px', position: 'absolute', top: '28px' }}>Домофоны в Солнечногорске</a>
                <iframe src="https://yandex.ru/map-widget/v1/?ll=36.987107%2C56.184337&mode=search&oid=1027510868&ol=biz&sctx=ZAAAAAgBEAAaKAoSCSRHOgMjfUJAEUw2HmyxF0xAEhIJidS0i2mmtz8RKLfte9Rfsz8iBgABAgMEBSgKOABAg1RIAWI7cmVsZXZfcmFua2luZ19oZWF2eV9yZWxldl93b3JsZF9mb3JtdWxhPTAuNzpsM19kYzcyMDM0Ml9leHBiOnJlbGV2X3JhbmtpbmdfaGVhdnlfcmVsZXZfbWFwc19mb3JtdWxhPTAuNjpsM19kYzcyMDM0Ml9leHBqAnJ1nQHNzMw9oAEAqAEAvQF6VhkYwgEF1KT66QOCAhPRgdC%2B0L3QtdGCIC0g0LTQvtC8igIAkgIAmgIMZGVza3RvcC1tYXBz&sll=36.987107%2C56.184337&source=serp_navig&sspn=0.015407%2C0.005110&text=%D1%81%D0%BE%D0%BD%D0%B5%D1%82%20-%20%D0%B4%D0%BE%D0%BC&z=17" allowFullScreen></iframe>
              </div>
            </MapContainer>
          </InfoAndMapContainer>
          <InfoAndMapContainer
            variants={infoAndMapVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
          >
            <ContactContainer>
              <Subtitle>Офис в г. Клин</Subtitle>
              <Address>
                141606 Московская область, г.Клин, ул.Мира, д.25 офис 9
              </Address>
              <ContactInfo href="tel:+74962437696">Тел: +7 (4962) 3-76-96</ContactInfo>
              <ContactInfo href="mailto:mail@sonet-dom.ru">E-Mail: mail@sonet-dom.ru</ContactInfo>
              <WorkingHoursContainer>
                <Subtitle>Время работы:</Subtitle>
                <WorkingHoursTable>
                  <thead>
                    <tr>
                      <TableHeader>Дни недели</TableHeader>
                      <TableHeader>Время</TableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <TableCell>Понедельник - Четверг</TableCell>
                      <TableCell>09:00 - 17:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Пятница</TableCell>
                      <TableCell>09:00 - 17:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Обед</TableCell>
                      <TableCell>13:00 - 14:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Суббота (дежурный день)</TableCell>
                      <TableCell>10:00 - 14:00</TableCell>
                    </tr>
                    <tr>
                      <TableCell>Воскресенье</TableCell>
                      <TableCell>Выходной</TableCell>
                    </tr>
                  </tbody>
                </WorkingHoursTable>
              </WorkingHoursContainer>
            </ContactContainer>
            <MapContainer>
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                <a href="https://yandex.ru/maps/org/sonet_dom/202525068006/?utm_medium=mapframe&utm_source=maps" style={{ color: '#eee', fontSize: '12px', position: 'absolute', top: '0px' }}>Сонет Дом</a>
                <a href="https://yandex.ru/maps/10733/klin/category/intercoms/184105336/?utm_medium=mapframe&utm_source=maps" style={{ color: '#eee', fontSize: '12px', position: 'absolute', top: '14px' }}>Домофоны в Клину</a>
                <a href="https://yandex.ru/maps/10733/klin/category/security_and_alarm_systems/184105350/?utm_medium=mapframe&utm_source=maps" style={{ color: '#eee', fontSize: '12px', position: 'absolute', top: '28px' }}>Системы безопасности и охраны в Клину</a>
                <iframe src="https://yandex.ru/map-widget/v1/?ll=36.732470%2C56.321595&mode=search&oid=202525068006&ol=biz&sctx=ZAAAAAgBEAAaKAoSCSRHOgMjfUJAEUw2HmyxF0xAEhIJidS0i2mmtz8RKLfte9Rfsz8iBgABAgMEBSgKOABA7VNIAWI7cmVsZXZfcmFua2luZ19oZWF2eV9yZWxldl93b3JsZF9mb3JtdWxhPTAuNzpsM19kYzcyMDM0Ml9leHBiOnJlbGV2X3JhbmtpbmdfaGVhdnlfcmVsZXZfbWFwc19mb3JtdWxhPTAuNjpsM19kYzcyMDM0Ml9leHBqAnJ1nQHNzMw9oAEAqAEAvQF6VhkYwgEG5pW9u%2FIFggIT0YHQvtC90LXRgiAtINC00L7QvIoCAJICAJoCDGRlc2t0b3AtbWFwcw%3D%3D&sll=36.732470%2C56.321595&source=serp_navig&sspn=0.022871%2C0.007558&text=%D1%81%D0%BE%D0%BD%D0%B5%D1%82%20-%20%D0%B4%D0%BE%D0%BC&z=16.43" allowFullScreen></iframe>
              </div>
            </MapContainer>
          </InfoAndMapContainer>
        </Section>
      </GlobalStyles>
    </>
  );
};

export default Contacts;